import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { keys } from 'lodash';
import FormModal from '../FormModal.jsx';
import {
  retrieveOrgGroups,
  selectFlattenedOrgGroups,
} from '../../modules/orgGroups.js';
import {
  createEmployee,
  retrieveSimplifiedEmployees,
  selectRetrieveSimplifiedEmployeesState,
} from '../../modules/employees.js';
import { selectCurrentOrganizationId } from '../../modules/auth.js';
import ManageGroupForm from './ManageGroupForm.jsx';
import { validateEmployee } from '../../containers/employees/utils/index.js';
import { mapFinalFormErrors } from '../../lib/utils.js';

/* eslint-disable camelcase */

const mapStateToProps = (state) => ({
  organizationId: selectCurrentOrganizationId(state),
  flattenedGroups: selectFlattenedOrgGroups(state),
  retrieveEmployeesPending:
    selectRetrieveSimplifiedEmployeesState(state).pending,
});

const mapDispatchToProps = {
  retrieveSimplifiedEmployees,
  createEmployee,
  retrieveOrgGroups,
};

const ManageGroupModal = ({
  organizationId,
  onSubmit,
  showFormModal,
  onHideFormModal,
  group,
  flattenedGroups,
  retrieveSimplifiedEmployees,
  retrieveEmployeesPending,
  additionalSearchParams,
  hideSelectAll,
  createEmployee,
  retrieveOrgGroups,
}) => {
  const [formState, setFormState] = useState(null);
  const [groupEmployees, setGroupEmployees] = useState({});
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [newEmployeeEntities, setNewEmployeeEntities] = useState({
    group: [],
    parent: [],
  });

  const groupEntity = () => flattenedGroups.find((g) => g.id === group.id);

  const updateEntities = (payload) => {
    setNewEmployeeEntities((prevEntities) => ({
      ...prevEntities,
      ...payload,
    }));
  };

  const onToggleEmployeeFrom = () => {
    setShowEmployeeForm(!showEmployeeForm);
    setErrors({});
    updateEntities({ parent: [] });
  };

  const onEmployeeParentSelect = (employee) => {
    updateEntities({
      parent:
        newEmployeeEntities.parent.length &&
        newEmployeeEntities.parent[0].id === employee.id
          ? []
          : [employee],
    });
  };

  const handleSelectEmployee = useCallback((employee) => {
    setFormState((prevState) => ({
      ...prevState,
      employee_ids: prevState.employee_ids.includes(employee.id)
        ? prevState.employee_ids.filter((eId) => eId !== employee.id)
        : [...prevState.employee_ids, employee.id],
    }));

    setGroupEmployees((prevState) => {
      return { ...prevState, [employee.id]: employee };
    });
  }, []);

  const handleSelectAllEmployees = useCallback(
    (groupId, employee_ids, entities) => {
      setFormState((prevState) => ({
        ...prevState,
        employee_ids,
      }));
      setGroupEmployees((prevState) => ({
        ...prevState,
        ...entities,
      }));
    },
    [formState]
  );

  const onSubmitSuccess = () => {
    setFormState(null);
    setGroupEmployees([]);
  };

  const onAddEmployeeSubmit = (values) => {
    const errors = validateEmployee(values);
    if (keys(errors).length) {
      setErrors(errors);
    } else {
      const employee = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        position: values.position,
        location: values.location,
        externalId: values.externalId,
        groupId: newEmployeeEntities.group[0].id,
        parentId: newEmployeeEntities.parent.length
          ? newEmployeeEntities.parent[0].id
          : null,
      };

      return createEmployee(organizationId, employee)
        .then(({ entities, result }) => {
          const { objectType, objectId } = additionalSearchParams;
          handleSelectEmployee(entities.employees[result]);
          updateEntities({ parent: [] });
          setShowEmployeeForm(false);
          retrieveOrgGroups({ objectType, objectId });
          setErrors({});
        })
        .catch((err) => setErrors(mapFinalFormErrors()(err)));
    }
  };

  useEffect(() => {
    if (showFormModal) {
      setShowEmployeeForm(false);
      setFormState(group);
      updateEntities({ group: [group] });
      group.employee_ids.length &&
        retrieveSimplifiedEmployees({
          groupIds: [group.id],
          autocomplete: false,
          active: true,
          ...additionalSearchParams,
        }).then(({ entities }) => {
          if (entities.employeesAutocomplete) {
            setGroupEmployees(entities.employeesAutocomplete);
          }
        });
    }
  }, [showFormModal]);

  return formState && showFormModal ? (
    <FormModal
      createResource={() => Promise.resolve(onSubmit(formState))}
      onSubmitSuccess={onSubmitSuccess}
      onHide={onHideFormModal}
      title={`Manage - ${formState.name} ${formState.archived ? '(archived)' : ''}`}
      show={showFormModal}
      hideFooter={showEmployeeForm}
    >
      <ManageGroupForm
        disabledSelectAll={retrieveEmployeesPending}
        hideSelectAll={hideSelectAll}
        group={formState}
        groupEntity={groupEntity()}
        groupEmployees={groupEmployees}
        handleSelect={handleSelectEmployee}
        handleSelectAll={handleSelectAllEmployees}
        pending={retrieveEmployeesPending}
        additionalSearchParams={additionalSearchParams}
        newEmployeeEntities={newEmployeeEntities}
        showEmployeeForm={showEmployeeForm}
        setShowEmployeeForm={onToggleEmployeeFrom}
        onParentSelect={onEmployeeParentSelect}
        onAddEmployeeSubmit={onAddEmployeeSubmit}
        errors={errors}
      />
    </FormModal>
  ) : null;
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageGroupModal);
